import React from 'react';

import { AutoField } from 'uniforms-material';
import { Box, Grid, TextField, Typography } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import { alertRoutine } from '../../../redux/Alerts/routines';

import { store, i18n } from '../../../index';

import { capitalizeName } from '../../../utils/string-helpers.js';

import { putPatientRoutinePromiseCreator } from '../../../redux/Patients/routines';

import { history } from '../../../utils/configureStore';

import schema from './patientLinkFormSchema';

import CustomPatientSelectorField from '../../uniforms/CustomPatientLazySelectorField';
import { patientFormActionCreator2 } from './patientFormActionCreator2.js';
import {
  buildExternalSyncPutPayload,
  formatExternalSyncValue,
  shouldRunExternalSyncPut,
} from '../../../utils/externalPatientSync';

const errorActionCreator = () => (
  alertRoutine.show({
    type: 'snack',
    title: i18n.t('common:interface.an error occured'),
    severity: 'error',
  })
);

const LinkPatientForm = ({ patient }) => {
  return (
    <div>
      <AutoField name="patient" initialSearch={`${patient.lastname} ${patient.firstname}`} component={CustomPatientSelectorField} />
    </div>
  );
};

const ExternalSyncForm = ({ differences }) => {
  return (
    <div>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {i18n.t('form:patient.link.sync.description')}
      </Typography>

      {differences.map((difference) => (
        <Box key={difference.field} mb={2}>
          <AutoField name={`fields.${difference.field}`} />
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                label={i18n.t('form:patient.link.sync.current')}
                value={formatExternalSyncValue(difference.field, difference.currentValue, (key, options) => i18n.t(key, options))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2} style={{ textAlign: 'center' }}>
              <ArrowForwardIcon color="action" />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                label={i18n.t('form:patient.link.sync.external')}
                value={formatExternalSyncValue(difference.field, difference.incomingValue, (key, options) => i18n.t(key, options))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </div>
  );
};

const getExternalSyncFieldTitle = (field) => {
  const fieldKey = `form:patient.${field}`;

  if (i18n.exists(`${fieldKey}.label`)) {
    return i18n.t(`${fieldKey}.label`);
  }

  if (i18n.exists(fieldKey)) {
    const translatedField = i18n.t(fieldKey, { returnObjects: true });

    if (typeof translatedField === 'string') {
      return translatedField;
    }

    if (translatedField && typeof translatedField === 'object' && typeof translatedField.label === 'string') {
      return translatedField.label;
    }
  }

  if (field.startsWith('assure_')) {
    const baseFieldKey = `form:patient.${field.replace(/^assure_/, '')}`;
    if (i18n.exists(baseFieldKey)) {
      return `${i18n.t('form:patient.tabs.assure')} - ${i18n.t(baseFieldKey)}`;
    }
  }

  return field;
};

const createExternalSyncFormSchema = (differences = []) => {
  const diffFields = differences.reduce((result, diff) => {
    result[diff.field] = {
      type: 'boolean',
      title: getExternalSyncFieldTitle(diff.field),
    };

    return result;
  }, {});

  return {
    type: 'object',
    properties: {
      fields: {
        type: 'object',
        properties: diffFields,
      },
    },
    required: ['fields'],
  };
};

const runExternalSyncPut = async ({ patient, externalPatient, service, externalId, selectedFields, ensureLink = false, redirectToPatientId }) => {
  const { values } = buildExternalSyncPutPayload({
    patient,
    externalPatient,
    service,
    externalId,
    selectedFields,
    ensureLink,
  });

  if (shouldRunExternalSyncPut(values)) {
    await putPatientRoutinePromiseCreator({
      values,
      id: patient.id,
    }, store.dispatch);
  }

  if (redirectToPatientId) {
    history.push(`/patient/${redirectToPatientId}`);
  }

  return true;
};

export const patientExternalSyncActionCreator = ({
  service,
  externalId,
  patient,
  externalPatient,
  ensureLink = false,
  redirectToPatientId,
}) => {
  if (!patient?.id || !externalPatient || !service || !externalId) {
    return null;
  }

  const { pendingDifferences } = buildExternalSyncPutPayload({
    patient,
    externalPatient,
    service,
    externalId,
    ensureLink,
  });

  if (!pendingDifferences.length) {
    return null;
  }

  const formSchema = createExternalSyncFormSchema(pendingDifferences);
  const model = {
    fields: pendingDifferences.reduce((result, diff) => {
      result[diff.field] = true;
      return result;
    }, {}),
  };

  return alertRoutine.show({
    id: 'patientExternalSyncForm',
    type: 'form',
    maxWidth: 'sm',
    schema: formSchema,
    model,
    title: i18n.t('form:patient.link.sync.title'),
    content: <ExternalSyncForm differences={pendingDifferences} />,
    actionTitle: i18n.t('form:patient.link.sync.apply'),
    action: async (values, progress) => {
      try {
        progress.handleTrigger();

        const selectedFields = Object.entries(values?.fields || {})
          .filter(([, shouldUpdate]) => !!shouldUpdate)
          .map(([field]) => field);

        await runExternalSyncPut({
          patient,
          externalPatient,
          service,
          externalId,
          selectedFields,
          ensureLink,
          redirectToPatientId,
        });

        return true;
      } catch (error) {
        store.dispatch(errorActionCreator());
        return false;
      } finally {
        progress.handleFulfill();
      }
    },
  });
};

export const patientLinkFormActionCreator = (service, externalId, patient) => {
  if (patient) {
    patient.firstname = capitalizeName(patient.firstname);
    patient.lastname = patient.lastname.toUpperCase();
    console.log('patientLinkFormActionCreator', patient);
  }

  return (
    alertRoutine.show({
      id: 'patientLinkForm',
      type: 'form',
      maxWidth: 'xs',
      schema,
      title: i18n.t('form:patient.link.title'),
      content: <LinkPatientForm patient={patient} />,
      actionTitle: i18n.t('form:patient.link.link'),
      secondaryActions: [
        {
          title: i18n.t('form:patient.link.add'),
          action: async (parentAlertId) => {
            console.log('secondaryActions', patient);
            store.dispatch(patientFormActionCreator2(patient, parentAlertId));
          },
        },
      ],
      action: async (values, progress) => {
        console.log(values);

        try {
          progress.handleTrigger();

          const selectedPatient = values.patient;
          const { pendingDifferences } = buildExternalSyncPutPayload({
            patient: selectedPatient,
            externalPatient: patient,
            service,
            externalId,
            ensureLink: true,
          });

          if (pendingDifferences.length) {
            store.dispatch(patientExternalSyncActionCreator({
              service,
              externalId,
              patient: selectedPatient,
              externalPatient: patient,
              ensureLink: true,
              redirectToPatientId: selectedPatient.id,
            }));

            return true;
          }

          await runExternalSyncPut({
            patient: selectedPatient,
            externalPatient: patient,
            service,
            externalId,
            selectedFields: [],
            ensureLink: true,
            redirectToPatientId: selectedPatient.id,
          });

          return true;
        } catch (error) {
          store.dispatch(errorActionCreator());
          return false;
        } finally {
          progress.handleFulfill();
        }
      },
    })
  );
};
